import React from 'react';
import { fabric } from 'fabric';
import { type AhaSlateEditor } from '@aha-app/aha-editor-v2';
import SmartGuideBehaviors from '../objects/SmartGuideBehaviors';
import CollaborationBehaviors from '../objects/CollaborationBehaviors';
import tools from '../tools';
import ShapeBase from '../shapes/ShapeBase';
import FrameMemberBehaviors from '../objects/FrameMemberBehaviors';
import { type EmbeddedTextFormatQueryable } from '../objects/EmbeddedTextBehaviorsV2';
import { oncePerEvent } from '../utils/batching';
import LockBehaviors from '../objects/LockBehaviors';
import { type RecordId } from '../types';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import {
  hasCommentableBehaviors,
  hasDataStoreRecordBehaviors,
  hasEmbeddedTextBehaviors,
  hasFrameBehaviors,
  isConnector,
  isSimpleGroup,
  isSimpleSelection,
} from '../objects/guards';
import { type ToolList } from './ObjectBehaviors';
import Connector, { type Attachment } from '../objects/Connector';
import ReadOnlyBehaviors from '../objects/ReadOnlyBehaviors';
import type WhiteboardController from '../controllers/WhiteboardController';
import { sameObjectType } from '../utils/objects';
import SimpleSelection from './SimpleSelection';
import ShapePersistentControlBehaviors from '../objects/ShapePersistentControlBehaviors';
import VotingBehaviors from '../objects/VotingBehaviors';
import { type MountedObjects } from '@lib/canvasx/canvasx';
import { zManagerMove, zManagerSortCollection } from '../utils/zManager';
import * as Sentry from '@sentry/browser';
import { isConnectorLabel } from '../objects/LabelBehaviors';

const min = fabric.util.array.min;
const max = fabric.util.array.max;
const multiply = fabric.util.multiplyTransformMatrices;

// Replacement for the Fabric Group class that does not apply its own transform
// to the objects it contains. This means that operations on the group are
// immediately applied to the group members. This is more consistent with
// collaboration - especially for active selections where the collaborators
// do not see the selection as a group.
//
// Note that objects access their group through the `.simpleGroup` property
// instead of `.group` so that the underlying fabric code never knows that
// anything is grouped. This is the trick that disables all of the existing
// fabric group behaviors.

interface Collection {
  remove(obj: fabric.Object): void;
  size(): number;
}

interface SimpleGroup
  extends fabric.Object,
    LockBehaviors,
    SmartGuideBehaviors,
    ReadOnlyBehaviors,
    CollaborationBehaviors,
    Collection,
    FrameMemberBehaviors,
    VotingBehaviors,
    ShapePersistentControlBehaviors {}

@LockBehaviors
@SmartGuideBehaviors
@ReadOnlyBehaviors
@CollaborationBehaviors
@FrameMemberBehaviors
@VotingBehaviors
@ShapePersistentControlBehaviors
class SimpleGroup extends fabric.Object implements EmbeddedTextFormatQueryable {
  type = 'group';

  id: RecordId;
  _objects: fabric.Object[];
  simpleGroup: this;
  isMoving?: boolean;

  mountedObjects: MountedObjects = {};

  // Groups only support uniform scaling. This is to ensure that a group
  // can't non-uniformly scale a contained object that doesn't support uniform
  // scaling (like a note).
  uniformScaling = true;
  noScaleCache = false;
  objectCaching = false;

  snapAngle = 45;
  snapThreshold = 5;
  borderOpacityWhenMoving = 0.1;

  transparentCorners = false;

  // Keys that will be serialized for saving and sending to remote systems.
  serializedKeys = ['z', 'objects', 'container', 'layoutPosition'];

  // see Canvas.collectionNeedsSort - this works in the same way
  collectionNeedsSort: boolean = true;

  constructor(
    objects: fabric.Object[],
    options: { locked?: boolean } & Record<string, unknown>
  ) {
    super({});

    this.cornerStyle = 'circle';

    this._objects = (objects || []).filter(o => !!o.selectable);
    for (let i = this._objects.length; i--; ) {
      this._objects[i].simpleGroup = this;
      // HACK: We need to fire the event *after* the constructor has completed so
      // that the type is set correctly. Otherwise for SimpleSelection subclasses
      // the type will be set to 'group' in the event callback since the
      // SimpleSelection constructor has not yet run.
      // TODO: Remove this timeout and make the code deterministic.
      setTimeout(() => {
        const object = this._objects[i];
        // TODO remove this once SimpleGroup is separated from SimpleSelection
        // This is a workaround to fix an issue where the group:added event fires
        // after applyRemoteOperation has processed, meaning any updates triggered
        // by the group:added event will be processed as an update by this user.
        // If they do not have edit permissions, the attempted update will be rejected
        // and trigger a rollback.
        // We suppress collaboration specifically for this event as we have identified
        // it as a known problem; suppressing at a lower level (e.g., in ClientBase)
        // could mask legitimate bugs.
        const fireEvent = () => object.fire('group:added');
        if (
          hasDataStoreRecordBehaviors(object) &&
          !this.controller.props.allowUpdate
        ) {
          object.withoutCollaboration(fireEvent);
        } else {
          fireEvent();
        }
      });
    }

    this.initializeLockBehaviors(
      options.locked ?? this._objects.some(object => object.locked)
    );

    super.setOptions(options || {});
    this.syncFromObjects();

    this.registerGroupEvents();
    this.needsZoomEvents();
    this.initializeSmartGuides();
    this.initializeFrameMember();

    // Remove  the controls we don't support.
    this.setControlsVisibility({
      mt: false,
      mb: false,
      ml: false,
      mr: false,
    });

    this.controls.mtr = ShapeBase.rotateControl();

    this.setRotationControlVisibility();
  }

  // Don't show the rotation control on a group or selection unless
  // every child object can be rotated
  setRotationControlVisibility() {
    const rotateVisible =
      this._objects.length > 0 &&
      this._objects.every(obj => obj.isControlVisible('mtr'));

    this.setControlsVisibility({
      mtr: rotateVisible,
    });
  }

  registerGroupEvents() {
    ['moving', 'moved', 'scaling', 'scaled', 'rotating', 'rotated'].forEach(
      eventName => this.on(eventName, () => this.transformObjects(eventName))
    );

    this.on('removed', event => {
      this.forEachObjectDeep(object => {
        object.fire('removed', event);
      });
    });

    this.on('mousedown', eventData => {
      if (isSimpleSelection(this)) {
        this.forEach(obj => {
          if (hasFrameBehaviors(obj) && obj.frameBehaviorsEnabled) {
            obj.startMoveWithContents(eventData);
          }
        });
      }
      // Reset preserveObjectStacking to true. It may have been set to false
      // after a double click but not reset if another object was not selected.
      if (!this.canvas.preserveObjectStacking) {
        this.canvas.preserveObjectStacking = true;
      }

      this.prepareForTransform(eventData);
    });
    this.on('duplicated', () => {
      // Sync the group after duplication to ensure the group dimensions are correct.
      this.syncFromObjects();
      this.prepareForTransform();
    });

    this.on('mousedblclick', eventData => {
      if (this.locked) return;
      if (this.type === 'activeSelection') {
        return;
      }
      // Select the object that was double-clicked.
      const pointer = this.canvas.getPointer(eventData.e, true);

      // See if a sub-object was clicked.
      const target = this.canvas._searchPossibleTargets(this._objects, pointer);
      if (target) {
        this.canvas.replaceSelectionWith(target);

        // We need to change the canvas.preserveObjectStacking behavior while
        // a group element is selected, otherwise any click over the group
        // will just reselect the group. This will be reset again by the
        // canvas when the selection changes or the group is clicked again.
        this.canvas.preserveObjectStacking = false;
      }
    });

    // pass mouse events to any children that care about them, including those in nested groups
    this.on('mousemove', this.handleMouseMove);
    this.on('mousewheel', this.handleMouseMove);
    this.on('mouseout', this.handleMouseOut);
  }

  handleMouseMove(eventData: fabric.IEvent) {
    const pointer = this.canvas.getPointer(eventData.e, true);

    // in case some of the group's members have been removed from the canvas, filter them out here
    // otherwise we get downstream errors
    const objects = this._objects.filter(obj => obj.canvas);

    const target = this.canvas._searchPossibleTargets(objects, pointer);
    if (target && (isSimpleGroup(target) || hasEmbeddedTextBehaviors(target))) {
      target.handleMouseMove(eventData);
    }
  }

  handleMouseOut() {
    for (const obj of this._objects) {
      if (isSimpleGroup(obj) || hasEmbeddedTextBehaviors(obj)) {
        // note this isn't strictly accurate, as it's mouseout for the top-level element,
        // not the child, but it's still useful for things like clearing link hover state
        obj.handleMouseOut();
      }
    }
  }

  deleted(controller: WhiteboardController) {
    const newObjects = this._objects;
    for (const obj of newObjects) {
      obj.selectable = true;
      controller.canvas.addObjects([obj]);
    }
  }

  get votingResultIcon(): JSX.Element {
    return <i className='fa-regular fa-object-group' />;
  }

  get votingResultSummary(): string {
    return `A group of ${this._objects.length} objects`;
  }

  // Groups and their members must have a _currentTransform set before firing any
  // events on them. This is normally handled in mousedown and duplicated, but in
  // certain cases we may need to set the transform manually (e.g., on load for
  // groups in layout objects)
  maybePrepareForTransform() {
    if (this.getObjects().some(obj => !obj._currentTransform)) {
      this.prepareForTransform();
    }
  }

  // Reset the relative transform for group members to the group in certain situations.
  // This is crucial for LayoutBehaviors, which may conflict with other objects' transforms
  // e.g., when dragging a group into a layout object as part of an activeSelection.
  // We need to remove the activeSelection's transform on the group and set the
  // group members' transform relative to the group.
  resetTransform() {
    this._currentTransform = null;
    this.prepareForTransform();
  }

  prepareForTransform(eventData?: fabric.IEvent<MouseEvent>) {
    // Prepare the transform for group manipulation.
    const groupTransform = this.calcTransformMatrix();
    const invertedGroupTransform = fabric.util.invertTransform(groupTransform);
    this.forEach(object => {
      object.relationship = multiply(
        invertedGroupTransform,
        object.calcTransformMatrix()
      );
      object._currentTransform = {
        original: fabric.util.saveObjectTransform(object),
        last: fabric.util.saveObjectTransform(object),
      };

      if (
        eventData?.e.type === 'mousedown' &&
        hasCommentableBehaviors(object)
      ) {
        object.prepareForEvent(eventData);
      }

      if (object instanceof Connector) {
        // Save a copy of the connector points in local coordinates so that we
        // can freely change the top/left properties of the connection object
        // without affecting the results of `toLocalPoint` later.
        object._originalPoints = object.points.map(point =>
          object.toLocalPoint(point as fabric.Point, 'center', 'center')
        );
      }

      // If this is a nested group, prepare it for transform as well
      if (isSimpleGroup(object)) {
        object.prepareForTransform(eventData);
      }
    });
  }

  _attachmentInSelection(attachment: Attachment): boolean {
    return !attachment.object.locked && !!this.findObject(attachment.object.id);
  }

  transformObjects(eventName: string, options = { withoutBatching: false }) {
    // Batch events since this may be called directly, and then again by
    // SmartGuideBehaviors if the group is snapped.
    oncePerEvent(
      `${this.id}-transformObjects-${eventName}`,
      options.withoutBatching,
      () => {
        const thisTransform = this.calcTransformMatrix();
        this.forEach(object => {
          // if object.canvas is unset, assume the object has been removed,
          // so we should ignore it
          if (!object.canvas) {
            return;
          }

          // Connector should only be transformed if all attachments are in the
          // selection and the attachments are all unlocked
          if (
            isConnector(object) &&
            !object.attachments.every(
              attachment =>
                attachment == null || this._attachmentInSelection(attachment)
            )
          ) {
            return;
          }
          const newTransform = multiply(
            thisTransform,
            object.relationship ?? []
          );
          const opt = fabric.util.qrDecompose(newTransform);

          // Limit which objects can be rotated in a group.
          if (
            !['shape', 'path', 'stamp', 'group', 'mindMap'].includes(
              object.type
            )
          ) {
            opt.angle = 0;
          }

          // Connectors without attachments are transformed manually
          // (each point is transformed individually)
          if (
            object instanceof Connector &&
            object.numValidAttachments === 0 &&
            object._originalPoints
          ) {
            object.set({
              points: object._originalPoints.map(point => {
                const { x, y } = fabric.util.transformPoint(
                  point as fabric.Point,
                  newTransform,
                  false
                );

                return { x, y };
              }),
            });

            // Since we've manually transformed the points, we need to force a
            // recalculation of our position (top, left) and dimensions.
            object._setPositionDimensions();
          } else {
            object.set({
              angle: opt.angle,
              scaleX: opt.scaleX,
              scaleY: opt.scaleY,
            });
            object.setPositionByOrigin(
              { x: opt.translateX, y: opt.translateY } as fabric.Point,
              'center',
              'center'
            );
          }

          if (isSimpleGroup(object)) {
            // If this is a nested group, transform its children
            object.transformObjects(eventName, options);
          } else {
            if (!object._currentTransform) {
              Sentry.captureException(
                new Error(
                  `Missing transform for group member of type '${object.type}' in event '${eventName}'`
                )
              );
              return;
            }
            object.fire(eventName, { transform: object._currentTransform });
            object._currentTransform.last =
              fabric.util.saveObjectTransform(object);
          }
          object.setCoords();
        });
      }
    );
  }

  // Move the group and contents to the specified top/left position.
  moveToCoords({ left, top }: { left: number; top: number }) {
    this.prepareForTransform();
    this.set({ left: left || this.left, top: top || this.top });
    this.setCoords();
    this.fire('moved');
    // With this line enabled the connector gets detached from the shape because
    // it calls positionConnectors() out of sync with the shape.
    this.transformObjects('moved', { withoutBatching: true });
    this.synchronize();
  }

  render(ctx: CanvasRenderingContext2D) {
    if (this.isNotVisible()) {
      return;
    }
    if (
      this.canvas &&
      this.canvas.skipOffscreen &&
      !this.simpleGroup &&
      !this.isOnScreen()
    ) {
      return;
    }

    ctx.save();
    // @ts-expect-error private fabric method
    this._setupCompositeOperation(ctx);
    this.drawSelectionBackground(ctx);

    this.dirty = false;
    this.drawObject(ctx);

    this.borderColor = this.locked
      ? this.lockedBorderColor
      : this.unlockedBorderColor;

    ctx.restore();
  }

  drawObject(ctx: CanvasRenderingContext2D) {
    if (this.collectionNeedsSort) {
      zManagerSortCollection(this._objects);
      this.collectionNeedsSort = false;
    }

    for (let i = 0, len = this._objects.length; i < len; i++) {
      this._objects[i].render(ctx);
    }
    // @ts-expect-error private fabric method
    this._drawClipPath(ctx);
  }

  toActiveSelection(controller: WhiteboardController) {
    const objects = this._objects;
    const targetZ = this.z;
    const canvas = controller.canvas;
    this._objects = [];
    const options = this.toObject();
    delete options.objects;
    // @ts-expect-error ActiveSelection _is_ SimpleSelection (see overrides.ts), but TS doesn't
    // know that here (also, importing SimpleSelection in this file is circular, and breaks things)
    const activeSelection: SimpleSelection = new fabric.ActiveSelection([], {});
    activeSelection.set(options);
    activeSelection.type = 'activeSelection';
    activeSelection.canvas = canvas;
    activeSelection.controller = controller;
    canvas.remove(this);
    for (let i = objects.length - 1; i >= 0; i--) {
      const object = objects[i];
      object.simpleGroup = activeSelection;
      object.fire('group:removed');
      object.dirty = true;
    }

    // Ensure any connector labels are reattached to their connectors
    const connectors = objects.filter(isConnector);
    const labels = objects.filter(isConnectorLabel);
    if (connectors.length > 0 && labels.length > 0) {
      for (const obj of labels) {
        const connector = connectors.find(o => o.id === obj.connectorId);
        if (connector) {
          obj.initLabel(connector, obj.labelPosition, obj.angle);
        }
      }
    }

    activeSelection._objects =
      SimpleSelection.getFilteredObjectsForSelection(objects);
    activeSelection.setRotationControlVisibility();
    canvas._activeObject = activeSelection;
    activeSelection.setCoords();

    if (this.simpleGroup) {
      this.simpleGroup.removeWithUpdate(this);
    }

    zManagerMove(activeSelection._objects, { where: 'after', targetZ });

    return activeSelection;
  }

  // Make the group position match the contents.
  syncFromObjects() {
    const aX: number[] = [];
    const aY: number[] = [];
    const props = ['tr', 'br', 'bl', 'tl'];
    const objects = this._objects.filter(o => o.visible);
    const iLen = objects.length;
    const jLen = props.length;

    for (let i = 0; i < iLen; ++i) {
      const o = objects[i];
      // @ts-expect-error private fabric method
      const coords = o.calcACoords();
      for (let j = 0; j < jLen; j++) {
        const prop = props[j];
        aX.push(coords[prop].x);
        aY.push(coords[prop].y);
      }
      o.aCoords = coords;
    }

    this._getBounds(aX, aY);
    this.setCoords();
    delete this.relationship;

    // Propagate the sync to the parent if there are nested groups.
    if (this.simpleGroup) {
      this.simpleGroup.syncFromObjects();
    }
  }

  // Call synchronize on all objects in the group.
  synchronize(): void {
    this.forEach(obj => obj.synchronize());
  }

  _getBounds(aX: number[], aY: number[]) {
    // @ts-expect-error min is typed incorrectly
    const minXY = new fabric.Point(min(aX), min(aY));
    // @ts-expect-error max is typed incorrectly
    const maxXY = new fabric.Point(max(aX), max(aY));
    const top = minXY.y || 0;
    const left = minXY.x || 0;
    const width = maxXY.x - minXY.x || 0;
    const height = maxXY.y - minXY.y || 0;

    this.width = width;
    this.height = height;
    this.left = left;
    this.top = top;
    this.scaleX = 1;
    this.scaleY = 1;
    this.angle = 0;
  }

  addWithUpdate(object?: fabric.Object) {
    const nested = !!this.simpleGroup;

    if (object) {
      this._objects.push(object);
      object.simpleGroup = this;
      object._set('canvas', this.canvas);
      object.fire('added');
      object.fire('group:added');
      this.collectionNeedsSort = true;
    }
    this.syncFromObjects();
    this.dirty = true;

    if (nested) {
      this.simpleGroup.addWithUpdate();
    }

    return this;
  }

  removeWithUpdate(object: fabric.Object) {
    this.remove(object);
    this.syncFromObjects();
    this.setCoords();
    this.dirty = true;

    this.canvas?.controller.actionForceRerenderObjectToolbar();

    return this;
  }

  _onObjectAdded(object: fabric.Object) {
    this.dirty = true;
    object.simpleGroup = this;
    if (object.canvas !== this.canvas) {
      object._set('canvas', this.canvas);
      object.fire('added');
    }
    object.fire('group:added');
  }

  _onObjectRemoved(object: fabric.Object) {
    this.dirty = true;
    delete object.simpleGroup;
    object.fire('group:removed');
  }

  _set(keyOrPath: KeyPath, value: any): fabric.Object {
    if (keyOrPath === 'canvas') {
      this._objects.forEach(object => {
        if (object.canvas !== value) {
          object._set(keyOrPath, value);
          object.fire('added');
        }
        object.setCoords();
      });
    }

    return fabric.Object.prototype._set.call(this, keyOrPath, value);
  }

  destroy() {
    // when group is destroyed objects needs to get a repaint to be eventually
    // displayed on canvas.
    this._objects.forEach(object => object.set('dirty', true));
    // Let each object know it was removed *after* its state is restored.
    this._objects.forEach(object => {
      delete object.simpleGroup;
      object.setCoords();
    });
    return this;
  }

  shouldCache() {
    return false;
  }

  isOnACache() {
    return false;
  }

  // Override built-in method so we can treat connectors differently.
  _restoreObjectsState() {
    this._objects.forEach(object => {
      delete object.simpleGroup;
      object.synchronize();
      object.setCoords();
    });
    return this;
  }

  _setObjectOnObjects(fieldValues: Record<string, unknown>) {
    this.forEach(object => {
      object._setObjectOnObjects(fieldValues);
    });
  }

  // Returns the object identified by id
  findObject(id: string): fabric.Object | undefined {
    return this._objects.find(object => {
      // Groups can contain other groups, so we need to recurse down the tree.
      if (isSimpleGroup(object)) {
        return object.findObject(id);
      }
      return object.id === id;
    });
  }

  // Return the first non-group object.
  firstObject(): fabric.Object {
    const object = this.getObjects()[0];
    if (isSimpleGroup(object) || isSimpleSelection(object)) {
      return object.firstObject();
    } else {
      return object;
    }
  }

  // Iterate over a copy of all objects in the group.
  forEach(func: (obj: fabric.Object) => void) {
    const objects = this.getObjects();
    for (let i = 0, len = objects.length; i < len; i++) {
      func(objects[i]);
    }
    return this;
  }

  *objectsOfType<T>(func: (o: any) => o is T): Generator<T, any, unknown> {
    for (const o of this.getObjects()) {
      if (func(o)) {
        yield o;
      }
    }
  }

  *objectsOfTypeDeep<T>(func: (o: any) => o is T): Generator<T, any, unknown> {
    for (const o of this.getObjects()) {
      if (func(o)) {
        yield o;
      } else {
        if (isSimpleGroup(o)) {
          for (const inner of o.objectsOfTypeDeep(func)) {
            yield inner;
          }
        }
      }
    }
  }

  // Iterate over a copy of all objects in the entire tree, recursing into
  // nested groups.
  forEachObjectDeep(func: (obj: fabric.Object) => void) {
    const objects = this.getObjects();
    for (let i = 0, len = objects.length; i < len; i++) {
      func(objects[i]);
      const obj = objects[i];
      if (isSimpleGroup(obj)) {
        obj.forEachObjectDeep(func);
      }
    }
    return this;
  }

  applyTextProperty(
    func: (editor: AhaSlateEditor, obj: fabric.Object) => void
  ) {
    for (const member of this.objectsOfTypeDeep(hasEmbeddedTextBehaviors)) {
      member.applyTextProperty(func, true);
    }
  }

  selectionIsEntirely(textFormat: string) {
    for (const o of this.objectsOfTypeDeep(hasEmbeddedTextBehaviors)) {
      if (!o.selectionIsEntirely(textFormat)) {
        return false;
      }
    }
    return true;
  }

  tools(topLevel = true) {
    // Find the list of all tools available on all selected objects
    const objects = this.getObjects();
    const allTools = objects.map(object => object.tools?.(false) ?? []);

    let uniqueTools: ToolList = [];
    if (allTools.length) {
      const flattenedTools = allTools.map(tools =>
        Array.isArray(tools) ? tools.flat(10) : [tools]
      );

      // Find the intersection of all tool arrays
      uniqueTools = flattenedTools.reduce(
        (result, tools) => result.filter(v => tools.includes(v)),
        flattenedTools[0] ?? []
      );
    }

    // Remove tools that only work on a single record.
    uniqueTools = uniqueTools.filter(
      tool =>
        ![tool].flat(10).includes(tools.ToolOpenRecord) &&
        ![tool].flat(10).includes(tools.ToolLock) &&
        // Don't allow comments on groups
        ![tool].flat(10).includes(tools.ToolComment) &&
        // Only show component state if all objects are the same wireframe component
        !(
          !sameObjectType(objects) &&
          [tool].flat(10).includes(tools.ToolWireframeComponentStyle)
        )
    );

    // `topLevel` ensures we don't add tools for groups nested inside groups.
    if (
      topLevel &&
      isSimpleSelection(this) &&
      !this._objects.some(object => object.locked)
    ) {
      uniqueTools.push(
        [
          tools.ToolAlignDistribute,
          tools.ToolGroup,
          this.size() >= 2 ? tools.ToolConnectGroup : null,
          tools.ToolFilter,
        ].filter(Boolean)
      );
    }
    if (topLevel && isSimpleGroup(this)) {
      uniqueTools.push([tools.ToolUngroup]);
    }

    return uniqueTools;
  }

  needsZoomEvents() {
    const fireZoom = () => this.forEachObjectDeep(obj => obj.fire('zoom'));
    this.on('added', () => {
      this.canvas.on('zoom', fireZoom);
    });

    this.on('removed', () => {
      this.canvas.off('zoom', fireZoom);
    });
  }

  setCoords(skipCorners?: boolean) {
    this.forEachObjectDeep(object => {
      if (object.canvas) {
        object.setCoords(skipCorners);
      }
    });

    return super.setCoords(skipCorners);
  }

  objectsToJSON() {
    return this.getObjects()
      .filter(hasDataStoreRecordBehaviors)
      .map(obj => obj.uniqueId());
  }
}

export default SimpleGroup;
